/**
 * شاشة «المستخدمون والصلاحيات» — الحسابات والأدوار وطلبات الانضمام.
 * المسارات: `/admin/*` — عقدها في `app/routers/users.py`.
 *
 * قواعد مقفولة الملف ده بيحترمها:
 *   • **المعلم مالوش حساب هنا** — بيستقبل إشعارات تليجرام بس.
 *   • **محدش يقرا كلمات السر** — مفيش دالة هنا بتطلب هاش ولا بتعرضه.
 *   • اعتماد طلب الانضمام **بياخد القسم والدور من المدير**، مش من كلام الموظف.
 *     `claimed_department` بيتعرض «قال إنه في: …» وبس وما بيتبعتش في الاعتماد.
 */
import { ApiError, api } from "./client";
import type { ListMeta } from "./types";

/* ══════════════════════════════════════════════════════════════════
   الأنواع والقوائم المعتمدة
   ══════════════════════════════════════════════════════════════════ */

export type AdminRole = "member" | "coordinator" | "director" | "observer";

/** حالة الحساب — القيم من `users.status` في الباك إند */
export type AccountStatus =
  | "active"
  | "pending_email"
  | "pending_approval"
  | "pending_activation"
  | "rejected"
  | "suspended"
  | "left";

export type AdminUser = {
  id: string;
  full_name_ar: string;
  display_name_ar?: string | null;
  email?: string | null;
  username?: string | null;
  department?: string | null;
  extra_departments?: string[] | null;
  role: string;
  status: string;
  last_login_at?: string | null;
  created_at?: string | null;
  /** القسم اللي الموظف كتبه عن نفسه — استرشادي وبس */
  claimed_department?: string | null;
  /** تخصصات الموظف — للمقيّمة أساسًا (قرآن · تجويد · إسلاميات …) */
  subjects?: string[] | null;
  telegram?: { linked?: boolean; linked_at?: string | null } | null;
  is_owner?: boolean;
  version?: number;
};

export type AdminUserCounts = {
  active: number;
  pending_activation: number;
  pending_approval: number;
  telegram_unlinked: number;
};

export type UsersPage = {
  rows: AdminUser[];
  meta: ListMeta | null;
  counts: AdminUserCounts | null;
};

export type AuditEntry = {
  id: string;
  action: string;
  actor_user_id?: string | null;
  actor_type?: string | null;
  entity?: { type?: string | null; id?: string | null } | null;
  reason_ar?: string | null;
  at?: string | null;
};

/** الأقسام التمانية المعتمدة — نفس قيم `Department` في الباك إند */
export const DEPARTMENTS: readonly { value: string; label: string }[] = [
  { value: "student_support", label: "دعم الطلاب (المبيعات)" },
  { value: "accounts", label: "الحسابات" },
  { value: "admin_supervision", label: "الإشراف الإداري" },
  { value: "evaluation", label: "التقييم والترشيح" },
  { value: "hiring", label: "التوظيف" },
  { value: "training_recordings", label: "التدريب والتسجيلات" },
  { value: "technical_supervision", label: "الإشراف الفني" },
  { value: "management", label: "الإدارة" },
];

/** الأدوار الأربعة — «الدور بيحدّد الشاشات والأزرار اللي بتظهر للمستخدم» */
export const ROLES: readonly { value: AdminRole; label: string }[] = [
  { value: "member", label: "عضو" },
  { value: "coordinator", label: "منسق قسم" },
  { value: "director", label: "مدير" },
  { value: "observer", label: "مراقب" },
];

const DEPT_LABELS = new Map(DEPARTMENTS.map((d) => [d.value, d.label]));

/** تخصصات المقيّمة — ⛔ القيم لازم تطابق `Subject` في الباك إند بالحرف. */
export const SUBJECTS: readonly { value: string; label: string }[] = [
  { value: "quran", label: "قرآن" },
  { value: "tajweed", label: "تجويد" },
  { value: "noor_bayan", label: "نور البيان" },
  { value: "islamic_studies", label: "إسلاميات" },
  { value: "arabic_non_native", label: "عربي لغير الناطقين" },
  { value: "english", label: "إنجليزي" },
];

const SUBJECT_LABELS = new Map(SUBJECTS.map((s) => [s.value, s.label]));

export function subjectLabel(value: string): string {
  return SUBJECT_LABELS.get(value) ?? value;
}
const ROLE_LABELS = new Map(ROLES.map((r) => [r.value as string, r.label]));

/**
 * اسم القسم بالعربي.
 * ⚠️ قيمة مش في القايمة = خلل في البيانات مش حالة عادية ⇒ بتتسجّل ERROR
 *    وبيتعرض نص عربي واضح بدل المفتاح الإنجليزي.
 */
export function departmentLabel(value: string | null | undefined): string {
  if (!value) return "بلا قسم";
  const label = DEPT_LABELS.get(value);
  if (label) return label;
  console.error(`[admin] قسم مش موجود في قايمة الأقسام: ${value}`);
  return "قسم مش معروف";
}

export function roleLabel(value: string | null | undefined): string {
  if (!value) return "بلا دور";
  const label = ROLE_LABELS.get(value);
  if (label) return label;
  console.error(`[admin] دور مش موجود في قايمة الأدوار: ${value}`);
  return "دور مش معروف";
}

/** حالة الحساب زي ما بتتعرض في الجدول */
export function statusLabel(value: string | null | undefined): string | null {
  switch (value) {
    case "active":
      return null; // الحالة الطبيعية ما بتتوسمش
    // 🔴 **الاتنين كانوا «مستني تفعيل» — وعلاجهم مختلف تمامًا.**
    //    الخلط ده ضلّلنا فعلًا (٢٠٢٦-٠٩-١٩): افتكرنا إن موظفة سجّلت
    //    بنفسها هي حساب عمله إداري، فاتأخّر تشخيص عطل حقيقي.
    case "pending_activation":
      return "مستني تفعيل";      // إداري عمل الحساب — العلاج: دعوة
    case "pending_email":
      return "مستني تأكيد البريد"; // سجّل بنفسه — العلاج: يفتح رابط الإيميل
    case "pending_approval":
      return "مستني اعتماد";
    case "suspended":
      return "متوقّف";
    case "rejected":
      return "مرفوض";
    case "left":
      return "ساب الأكاديمية";
    default:
      return null;
  }
}

/* ══════════════════════════════════════════════════════════════════
   حارس الشكل — «رد فاضي مش نجاح»
   ══════════════════════════════════════════════════════════════════ */

function shapeError(path: string, got: unknown): ApiError {
  console.error(`[admin] رد مش متوقّع من ${path}`, got);
  return new ApiError(
    502,
    {
      code: "BAD_RESPONSE_SHAPE",
      message_ar: "الرد اللي جه من السيستم مش مفهوم — جرّب تاني أو بلّغ إدارة الأكاديمية",
    },
    null,
  );
}

function readCounts(raw: Record<string, number> | undefined): AdminUserCounts | null {
  if (!raw || typeof raw.active !== "number") return null;
  return {
    active: raw.active,
    pending_activation: raw.pending_activation ?? 0,
    pending_approval: raw.pending_approval ?? 0,
    telegram_unlinked: raw.telegram_unlinked ?? 0,
  };
}

/* ══════════════════════════════════════════════════════════════════
   ١) جدول المستخدمين
   ══════════════════════════════════════════════════════════════════ */

export async function listUsers(opts: {
  q?: string;
  department?: string;
  role?: string;
  page: number;
  perPage: number;
  withCounts?: boolean;
  signal?: AbortSignal;
}): Promise<UsersPage> {
  const res = await api.get<AdminUser[]>("/admin/users", {
    query: {
      // السيرفر بيرفض بحث بحرف واحد — بنمنعه من هنا بدل ما نوري خطأ للموظف
      q: opts.q && opts.q.trim().length >= 2 ? opts.q.trim() : undefined,
      department: opts.department || undefined,
      role: opts.role || undefined,
      page: opts.page,
      per_page: opts.perPage,
      with_counts: opts.withCounts ? true : undefined,
    },
    ...(opts.signal ? { signal: opts.signal } : {}),
  });
  if (!Array.isArray(res.data)) throw shapeError("/admin/users", res.data);
  return { rows: res.data, meta: res.meta ?? null, counts: readCounts(res.counts) };
}

export type CreateUserInput = {
  full_name_ar: string;
  email: string;
  department: string;
  role: AdminRole;
};

/** «إضافة مستخدم» — الحساب بيتعمل مستني تفعيل بلا كلمة سر، والتفعيل بدعوة */
export async function createUser(input: CreateUserInput): Promise<AdminUser> {
  const res = await api.post<AdminUser>("/admin/users", input);
  if (!res.data?.id) throw shapeError("/admin/users", res.data);
  return res.data;
}

/** «إرسال دعوة تفعيل» — بترجّع نص السيستم زي ما هو */
export async function inviteUser(userId: string): Promise<string> {
  const res = await api.post<{ message_ar?: string }>(
    `/admin/users/${encodeURIComponent(userId)}/invite`,
  );
  const message = res.data?.message_ar;
  if (typeof message !== "string" || !message) {
    throw shapeError("/admin/users/{id}/invite", res.data);
  }
  return message;
}

/**
 * 🔑 «كلمة سر مؤقتة» — **المسار الوحيد لتفعيل موظف والبريد لسه مش متظبّط.**
 *
 * ⛔ كان مبنيًّا في السيستم من غير أي زرار يوصله، فالمدير مالوش طريقة
 *    يدخّل حد أصلًا لما «إرسال دعوة تفعيل» يفشل (ومفيش بريد ⇒ بيفشل).
 *
 * ⚠️ الكلمة **بتتعرض مرة واحدة** — مش متخزّنة نصًّا وما بتترجعش تاني.
 *    والحساب `pending_activation` **بيتفعّل** مع إصدارها.
 */
export async function issueTempPassword(userId: string): Promise<{
  password: string;
  expiresInMinutes: number;
  warningAr: string;
}> {
  const path = `/admin/users/${encodeURIComponent(userId)}/temp-password`;
  const res = await api.post<{
    temp_password?: string;
    expires_in_minutes?: number;
    warning_ar?: string;
  }>(path);
  const password = res.data?.temp_password;
  if (typeof password !== "string" || !password) throw shapeError(path, res.data);
  return {
    password,
    expiresInMinutes: Number(res.data?.expires_in_minutes ?? 0),
    warningAr: res.data?.warning_ar ?? "",
  };
}

/**
 * تعديل الدور و/أو القسم لحساب موجود.
 *
 * 🔒 السيستم بيرفض إنك تدّي رتبة زيّك أو أعلى (إلا المالك)، وبيسجّل
 *    `user.role_changed` — تصعيد الصلاحيات أخطر فئة في السجل.
 */
export async function updateUserAccess(
  userId: string,
  input: { role?: AdminRole; department?: string; subjects?: string[]; extra_departments?: string[] },
): Promise<AdminUser> {
  const path = `/admin/users/${encodeURIComponent(userId)}`;
  const res = await api.patch<AdminUser>(path, input);
  if (!res.data?.id) throw shapeError(path, res.data);
  return res.data;
}

/* ══════════════════════════════════════════════════════════════════
   ٢) طلبات الانضمام — «طلبات انضمام (N)» فوق الجدول
   ══════════════════════════════════════════════════════════════════ */

export type JoinRequestsPage = { rows: AdminUser[]; meta: ListMeta | null };

export async function listJoinRequests(opts: {
  page: number;
  perPage: number;
  signal?: AbortSignal;
}): Promise<JoinRequestsPage> {
  const res = await api.get<AdminUser[]>("/admin/join-requests", {
    query: { page: opts.page, per_page: opts.perPage },
    ...(opts.signal ? { signal: opts.signal } : {}),
  });
  if (!Array.isArray(res.data)) throw shapeError("/admin/join-requests", res.data);
  return { rows: res.data, meta: res.meta ?? null };
}

/**
 * اعتماد طلب انضمام.
 * ⛔ القسم والدور بيتبعتوا من نموذج المدير. موظف كتب إنه «مدير» ما ياخدش
 *    دور مدير — `claimed_department` عمره ما بيدخل هنا.
 */
export async function approveJoinRequest(
  userId: string,
  decision: { department: string; role: AdminRole },
): Promise<AdminUser> {
  const res = await api.post<AdminUser>(
    `/admin/join-requests/${encodeURIComponent(userId)}/approve`,
    decision,
  );
  if (!res.data?.id) throw shapeError("/admin/join-requests/{id}/approve", res.data);
  return res.data;
}

/** رفض بسبب مكتوب إجباري. **الرفض ما بيمسحش السجل** — الحساب بيتقفل وبيفضل محفوظ. */
/**
 * «مسح» حساب — ناعم: السجل محفوظ بسبب، لكن الحساب بيختفي من القوائم وجلساته
 * بتموت **والبريد بيتحرّر** فصاحبه يقدر يسجّل من الأول.
 */
export async function archiveUser(userId: string, reasonAr: string): Promise<void> {
  const path = `/admin/users/${encodeURIComponent(userId)}/archive`;
  await api.post<void>(path, { reason_ar: reasonAr });
}

export async function rejectJoinRequest(
  userId: string,
  reasonAr: string,
): Promise<AdminUser> {
  const res = await api.post<AdminUser>(
    `/admin/join-requests/${encodeURIComponent(userId)}/reject`,
    { reason_ar: reasonAr },
  );
  if (!res.data?.id) throw shapeError("/admin/join-requests/{id}/reject", res.data);
  return res.data;
}

/* ══════════════════════════════════════════════════════════════════
   ٣) «سجل الدخول والتغييرات»
   ══════════════════════════════════════════════════════════════════ */

/** نصوص الأفعال بالعربي — أي فعل مش في الجدول بيتعرض بوصف عام مش بمفتاحه */
const AUDIT_LABELS: Record<string, string> = {
  "user.created": "اتعمل حساب جديد",
  "user.updated": "اتعدّلت بيانات حساب",
  "user.invited": "اتبعتت دعوة تفعيل",
  "user.unlocked": "اتفكّ قفل حساب",
  "user.suspended": "اتوقّف حساب",
  "user.archived": "اتمسح حساب",
  "user.activated": "اتفعّل حساب",
  "user.join_approved": "اتعتمد طلب انضمام",
  "user.join_rejected": "اترفض طلب انضمام",
  "user.telegram_unlinked": "اتفصل ربط تليجرام",
  "user.logged_in": "دخول للسيستم",
  "user.logged_out": "خروج من السيستم",
  "role.permissions_changed": "اتغيّرت صلاحيات دور",
};

export function auditLabel(action: string | null | undefined): string {
  if (!action) return "تغيير في السجل";
  return AUDIT_LABELS[action] ?? "تغيير في السجل";
}

export async function listAuditLog(opts: {
  limit: number;
  signal?: AbortSignal;
}): Promise<AuditEntry[]> {
  const res = await api.get<AuditEntry[]>("/admin/audit-log", {
    query: { limit: opts.limit },
    ...(opts.signal ? { signal: opts.signal } : {}),
  });
  if (!Array.isArray(res.data)) throw shapeError("/admin/audit-log", res.data);
  return res.data;
}
